import { UserOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import { useEffect, useState } from 'react';

interface Props {
  hasPhoto: boolean;
  fullName: string;
  /** Fetches the photo as an object URL. Only called when `hasPhoto` is true. */
  fetchUrl: () => Promise<string>;
  size?: number;
}

/**
 * Profile photo for an investor or a dealer.
 *
 * Photos sit behind JWT auth, so they can't be pointed at with a plain <img src>. This fetches the
 * blob once and falls back to initials when there is no photo (or the fetch fails). The fetcher is
 * injected rather than hardcoded so both sides of the book share one component.
 */
export default function PersonAvatar({ hasPhoto, fullName, fetchUrl, size = 96 }: Props) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!hasPhoto) {
      setSrc(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    fetchUrl()
      .then((url) => {
        objectUrl = url;
        if (cancelled) URL.revokeObjectURL(url);
        else setSrc(url);
      })
      .catch(() => setSrc(null));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // fetchUrl is a fresh closure on every render, so it is deliberately not a dependency —
    // including it would refetch the photo on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPhoto]);

  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  return (
    <Avatar
      size={size}
      src={src ?? undefined}
      icon={!src && !initials ? <UserOutlined /> : undefined}
      style={{
        backgroundColor: src ? undefined : '#FE9F43',
        fontSize: size / 2.8,
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      {!src && initials}
    </Avatar>
  );
}
